import { Field, Select, TextInput } from '../components/index.js';
import { fromApi, useFields, useSave } from '../lib/form.js';
import { t, toLatinDigits } from '../lib/i18n.js';
import { addUser, me, updateUser, users } from '../lib/khata.js';
import { FormErrorLine, SaveButton, Sheet } from './Sheet.js';

const digits = (v: string): string => toLatinDigits(v).replace(/\D/g, '').slice(0, 8);

export function UserSheet(props: { id?: string }) {
  const existing = props.id ? users.value.find((u) => u.id === props.id) : undefined;
  const self = !!existing && existing.id === me.value?.id;
  const [f, set] = useFields({
    name: existing?.name ?? '',
    login: '',
    pin: '',
    active: existing?.active === false ? 'no' : 'yes',
  });
  const [error, busy, save] = useSave();

  const onSave = () =>
    save(async () => {
      if (!f.name.trim()) return { message: t('user_needsName'), field: 'name' };
      if (existing) {
        const r = await updateUser(existing.id, {
          name: f.name,
          ...(self ? {} : { active: f.active === 'yes' }),
          ...(f.pin ? { pin: f.pin } : {}),
        });
        return r.ok ? t('user_saved', { name: r.value.name }) : fromApi(r.error);
      }
      if (f.pin.length < 4) return { message: t('user_needsPin'), field: 'pin' };
      const r = await addUser({ login: f.login, name: f.name, pin: f.pin });
      return r.ok ? t('user_added', { name: r.value.name }) : fromApi(r.error);
    });

  return (
    <Sheet
      title={existing ? t('sheet_editUser') : t('sheet_newUser')}
      footer={<SaveButton busy={busy} onClick={onSave} />}
    >
      <Field label={t('user_name')}>
        <TextInput
          value={f.name}
          onInput={(v) => set('name', v)}
          placeholder={t('user_namePlaceholder')}
          invalid={error?.field === 'name'}
          autoFocus={!existing}
          data-testid="user-name"
        />
      </Field>
      <Field label={t('user_login')}>
        {existing ? (
          <div class="input" style={{ opacity: 0.7 }}>
            {existing.login}
          </div>
        ) : (
          <TextInput
            value={f.login}
            onInput={(v) => set('login', v)}
            placeholder={t('user_loginPlaceholder')}
            invalid={error?.field === 'login'}
            autoCapitalize="none"
            autoComplete="off"
            inputMode="email"
            data-testid="user-login"
          />
        )}
      </Field>
      <div class="grid-2">
        <Field label={existing ? t('user_newPin') : t('user_pin')}>
          <TextInput
            type="password"
            inputMode="numeric"
            value={f.pin}
            onInput={(v) => set('pin', digits(v))}
            invalid={error?.field === 'pin'}
            autoComplete="new-password"
            placeholder={existing ? t('user_keepPin') : ''}
            data-testid="user-pin"
          />
        </Field>
        {existing && !self && (
          <Field label={t('account_status')}>
            <Select value={f.active} onChange={(v) => set('active', v)}>
              <option value="yes">{t('user_active')}</option>
              <option value="no">{t('user_disabled')}</option>
            </Select>
          </Field>
        )}
      </div>
      <p class="help">{t('user_help')}</p>
      <FormErrorLine error={error} />
    </Sheet>
  );
}
